import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, Button, Spinner, useToast } from "@toss-ads/ui";
import {
  listAdvertisers,
  listUserAdvertisers,
  setUserAdvertisers,
} from "../lib/api";
import { AdvertiserPickerModal } from "./AdvertiserPickerModal";
import type { Advertiser } from "../lib/types";

interface Props {
  userId: string;
}

export function UserDataTab({ userId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const { data: advertiserIds, isLoading: idsLoading } = useQuery({
    queryKey: ["admin", "users", userId, "advertisers"],
    queryFn: () => listUserAdvertisers(userId),
  });

  const ids = advertiserIds ?? [];

  const { data: advertisers, isLoading: advLoading } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
  });

  const allById = React.useMemo(
    () => new Map((advertisers ?? []).map((a) => [a.id, a])),
    [advertisers],
  );

  // 플랫폼은 lead 속성이라 광고주를 플랫폼으로 묶지 않는다 — 평면 리스트.
  const mine = React.useMemo(
    () => ids.map((id) => allById.get(id)).filter(Boolean) as Advertiser[],
    [ids, allById],
  );

  const update = useMutation({
    mutationFn: (next: string[]) => setUserAdvertisers(userId, next),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "users", userId, "advertisers"],
      });
      toast.success("광고주 권한이 변경되었습니다");
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "변경 실패"),
  });

  const isLoading = idsLoading || advLoading;

  const headerBar = (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-[13px] font-medium text-ink">담당 광고주</h3>
      <Button variant="primary" size="sm" onClick={() => setPickerOpen(true)}>
        담당 광고주 지정
      </Button>
    </div>
  );

  const picker = (
    <AdvertiserPickerModal
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      initiallySelected={ids}
      onConfirm={async (next) => {
        await update.mutateAsync(next);
      }}
    />
  );

  if (isLoading) {
    return (
      <div>
        {headerBar}
        <div className="rounded-lg border bg-surface py-16 flex justify-center text-ink-subtle">
          <Spinner size={20} />
        </div>
        {picker}
      </div>
    );
  }

  if (ids.length === 0) {
    return (
      <div>
        {headerBar}
        <div className="rounded-lg border bg-surface p-12 text-center text-[13px] text-ink-subtle">
          담당 광고주가 없습니다. "담당 광고주 지정" 으로 배정하세요.
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div>
      {headerBar}
      <div className="rounded-lg border bg-surface overflow-hidden">
        <ul>
          {mine.map((a) => (
            <li
              key={a.id}
              className="px-4 py-3 border-t border-border first:border-t-0 flex items-center justify-between gap-3"
            >
              <span className="text-[13px] text-ink truncate">{a.name}</span>
              <span className="text-[11px] text-ink-subtle">
                {a.active ? "활성" : "비활성"}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {picker}
    </div>
  );
}
